import { ChevronLeft, ChevronRight, Clock, MapPin, Plus, Search, Users } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useMeetingDrawer } from '../../hooks/useMeetingDrawer';
import { useToast } from '../../contexts/ToastContext';
import meetingService from '../../services/meetingService';
import { Meeting, MeetingListResponse } from '../../types';
import { format, isToday, parseISO } from 'date-fns';

type CalendarView = 'day' | 'week' | 'month';

interface MeetingCalendarProps {
  projectId?: string;
}

interface CalendarEvent {
  id: string;
  meeting: Meeting;
  startTime: Date;
  endTime: Date;
}

const MeetingCalendar: React.FC<MeetingCalendarProps> = ({ projectId }) => {
  const { openMeetingDrawer } = useMeetingDrawer();
  const { error, success } = useToast();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState<CalendarView>('month');
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Load calendar events
  const loadCalendarEvents = useCallback(async () => {
    setLoading(true);
    try {
      const startDate = getViewStartDate(currentDate, currentView);
      const endDate = getViewEndDate(currentDate, currentView);

      const params = {
        view: 'calendar',
        calendarView: currentView,
        dateFrom: startDate.toISOString(),
        dateTo: endDate.toISOString(),
      };

      const result = await meetingService.getMeetings(projectId, params);

      if (result.success && result.data) {
        // Check if result.data is MeetingListResponse or just array of meetings
        const meetings = Array.isArray(result.data)
          ? result.data
          : (result.data as MeetingListResponse).meetings || [];

        const calendarEvents = meetings
          .filter((meeting: Meeting) => {
            // Validate that both date fields exist and are valid strings
            return (
              meeting.startDateTime &&
              meeting.endDateTime &&
              typeof meeting.startDateTime === 'string' &&
              typeof meeting.endDateTime === 'string'
            );
          })
          .map((meeting: Meeting) => {
            try {
              return {
                id: meeting._id || meeting.id || '',
                meeting,
                startTime: parseISO(meeting.startDateTime),
                endTime: parseISO(meeting.endDateTime),
              };
            } catch (dateError) {
              console.warn('Invalid date format for meeting:', meeting._id, dateError);
              return null;
            }
          })
          .filter(Boolean); // Remove any null entries from failed date parsing
        setEvents(calendarEvents as CalendarEvent[]);
      } else {
        error(result.message || 'Failed to load calendar events');
      }
    } catch (err) {
      console.error('Error loading calendar events:', err);
      error('Failed to load calendar events');
    } finally {
      setLoading(false);
    }
  }, [currentDate, currentView, projectId, error]);

  useEffect(() => {
    loadCalendarEvents();
  }, [loadCalendarEvents]);

  // Filter events by search term
  const filteredEvents = events.filter(
    event =>
      !searchTerm ||
      event.meeting.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.meeting.location?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreateMeeting = (date?: Date) => {
    const selectedDate = date || new Date();
    const startTime = new Date(selectedDate);
    const endTime = new Date(selectedDate);

    // If no specific date provided (Add Meeting button), use current time + 30 minutes
    if (!date) {
      const now = new Date();
      startTime.setTime(now.getTime() + 30 * 60 * 1000); // Current time + 30 minutes
      endTime.setTime(now.getTime() + 90 * 60 * 1000); // Current time + 1.5 hours
    } else {
      // If clicking on calendar box, use that date but set reasonable default times
      const hour = startTime.getHours();

      // If clicking on a past date or early morning, set to next reasonable time
      if (startTime < new Date() || hour < 8) {
        startTime.setHours(Math.max(9, hour), 0, 0, 0);
        endTime.setHours(Math.max(10, hour + 1), 0, 0, 0);
      } else {
        startTime.setMinutes(0, 0, 0);
        endTime.setTime(startTime.getTime() + 60 * 60 * 1000); // +1 hour
      }
    }

    openMeetingDrawer({
      mode: 'create',
      projectId: projectId,
      defaultStartTime: startTime,
      defaultEndTime: endTime,
      onSave: () => {
        loadCalendarEvents();
        success('Meeting created successfully');
      },
      onDelete: () => {
        loadCalendarEvents();
        success('Meeting deleted successfully');
      },
    });
  };

  const handleEditMeeting = (meeting: Meeting) => {
    openMeetingDrawer({
      mode: 'edit',
      meeting: meeting,
      projectId: projectId,
      onSave: () => {
        loadCalendarEvents();
        success('Meeting updated successfully');
      },
      onDelete: () => {
        loadCalendarEvents();
        success('Meeting deleted successfully');
      },
    });
  };

  const handleViewMeeting = (meeting: Meeting) => {
    openMeetingDrawer({
      mode: 'view',
      meeting: meeting,
      projectId: projectId,
    });
  };

  const navigateDate = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);

    if (currentView === 'day') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
    } else if (currentView === 'week') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    } else if (currentView === 'month') {
      newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
    }

    setCurrentDate(newDate);
  };

  const getDateRangeText = () => {
    if (currentView === 'day') {
      return format(currentDate, 'EEEE, MMMM d, yyyy');
    } else if (currentView === 'week') {
      const startDate = getViewStartDate(currentDate, 'week');
      const endDate = getViewEndDate(currentDate, 'week');
      return `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d, yyyy')}`;
    } else {
      return format(currentDate, 'MMMM yyyy');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Calendar Header */}
      <div className="border-b border-gray-200 p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <h2 className="text-base font-medium text-gray-900">{getDateRangeText()}</h2>
          </div>

          <div className="flex items-center space-x-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search meetings..."
                className="pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
              />
            </div>

            {/* Create Meeting Button */}
            <button
              onClick={() => handleCreateMeeting()}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-brand-primary rounded-lg hover:bg-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-500 transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Meeting
            </button>
          </div>
        </div>

        {/* Calendar Navigation and View Controls */}
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigateDate('prev')}
              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>

            <button
              onClick={() => setCurrentDate(new Date())}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Today
            </button>

            <button
              onClick={() => navigateDate('next')}
              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>

          {/* View Switcher */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            {(['day', 'week', 'month'] as CalendarView[]).map(view => (
              <button
                key={view}
                onClick={() => setCurrentView(view)}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  currentView === view
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                {view.charAt(0).toUpperCase() + view.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Calendar Content */}
      <div className="p-3">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
            <span className="ml-3 text-gray-600">Loading calendar...</span>
          </div>
        ) : (
          <CalendarGrid
            view={currentView}
            date={currentDate}
            events={filteredEvents}
            onEventClick={handleViewMeeting}
            onEventEdit={handleEditMeeting}
            onDateClick={handleCreateMeeting}
          />
        )}
      </div>
    </div>
  );
};

// Calendar Grid Component
interface CalendarGridProps {
  view: CalendarView;
  date: Date;
  events: CalendarEvent[];
  onEventClick: (meeting: Meeting) => void;
  onEventEdit: (meeting: Meeting) => void;
  onDateClick?: (date: Date) => void;
}

const CalendarGrid: React.FC<CalendarGridProps> = ({
  view,
  date,
  events,
  onEventClick,
  _onEventEdit,
  onDateClick,
}) => {
  if (view === 'month') {
    return (
      <MonthView
        date={date}
        events={events}
        onEventClick={onEventClick}
        onDateClick={onDateClick}
      />
    );
  } else if (view === 'week') {
    return (
      <WeekView date={date} events={events} onEventClick={onEventClick} onDateClick={onDateClick} />
    );
  } else {
    return (
      <DayView date={date} events={events} onEventClick={onEventClick} onDateClick={onDateClick} />
    );
  }
};

// Month View Component
const MonthView: React.FC<{
  date: Date;
  events: CalendarEvent[];
  onEventClick: (meeting: Meeting) => void;
  onDateClick?: (date: Date) => void;
}> = ({ date, events, onEventClick, onDateClick }) => {
  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
  const _endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  const startDate = new Date(startOfMonth);
  startDate.setDate(startDate.getDate() - startOfMonth.getDay());

  const days = [];
  const current = new Date(startDate);

  for (let i = 0; i < 42; i++) {
    days.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }

  const getEventsForDay = (day: Date) => {
    return events.filter(event => event.startTime.toDateString() === day.toDateString());
  };

  return (
    <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-lg overflow-hidden">
      {/* Header */}
      {daysOfWeek.map(day => (
        <div key={day} className="bg-gray-50 p-3 text-center text-sm font-medium text-gray-700">
          {day}
        </div>
      ))}

      {/* Days */}
      {days.map((day, index) => {
        const dayEvents = getEventsForDay(day);
        const isCurrentMonth = day.getMonth() === date.getMonth();
        const isCurrentDay = isToday(day);

        return (
          <div
            key={index}
            className={`bg-white min-h-[120px] p-2 relative cursor-pointer hover:bg-gray-50 transition-colors ${
              !isCurrentMonth ? 'text-gray-400' : ''
            } ${isCurrentDay ? 'bg-brand-50 hover:bg-brand-100' : ''}`}
            onClick={() => onDateClick?.(day)}
          >
            <div
              className={`text-sm font-medium mb-1 ${
                isCurrentDay
                  ? 'text-brand-primary'
                  : isCurrentMonth
                    ? 'text-gray-900'
                    : 'text-gray-400'
              }`}
            >
              {day.getDate()}
            </div>

            <div className="space-y-1">
              {dayEvents.slice(0, 2).map(event => (
                <button
                  key={event.id}
                  onClick={e => {
                    e.stopPropagation();
                    onEventClick(event.meeting);
                  }}
                  className="w-full text-left p-1 text-xs bg-brand-100 text-brand-800 rounded hover:bg-brand-200 transition-colors truncate"
                >
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-brand-primary rounded-full flex-shrink-0"></div>
                    <span className="truncate font-medium">{event.meeting.title}</span>
                  </div>
                  <div className="text-[10px] text-brand-600 truncate mt-0.5">
                    {format(event.startTime, 'HH:mm')}
                    {event.meeting.location && ` • ${event.meeting.location}`}
                  </div>
                </button>
              ))}
              {dayEvents.length > 2 && (
                <button
                  onClick={e => {
                    e.stopPropagation();
                    // Could show a popover with all meetings for this day
                  }}
                  className="text-xs text-brand-600 hover:text-brand-700 px-1 py-0.5 hover:bg-brand-50 rounded transition-colors font-medium"
                >
                  +{dayEvents.length - 2} more
                </button>
              )}
            </div>

            {/* Click indicator for empty days */}
            {dayEvents.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                <div className="text-xs text-gray-400 bg-white px-2 py-1 rounded shadow border">
                  Click to add meeting
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Week View Component
const WeekView: React.FC<{
  date: Date;
  events: CalendarEvent[];
  onEventClick: (meeting: Meeting) => void;
  onDateClick?: (date: Date) => void;
}> = ({ date, events, onEventClick, onDateClick }) => {
  const startOfWeek = getViewStartDate(date, 'week');
  const days = Array.from({ length: 7 }, (_, i) => {
    const day = new Date(startOfWeek);
    day.setDate(day.getDate() + i);
    return day;
  });

  const hours = Array.from({ length: 24 }, (_, i) => i);

  const getEventsForDayAndHour = (day: Date, hour: number) => {
    return events.filter(event => {
      const eventDate = event.startTime.toDateString() === day.toDateString();
      const eventHour = event.startTime.getHours() === hour;
      return eventDate && eventHour;
    });
  };

  return (
    <div className="grid grid-cols-8 gap-px bg-gray-200 rounded-lg overflow-hidden">
      {/* Time column header */}
      <div className="bg-gray-50 p-3 text-center text-sm font-medium text-gray-700">Time</div>

      {/* Day headers */}
      {days.map(day => (
        <div key={day.toISOString()} className="bg-gray-50 p-3 text-center">
          <div className="text-sm font-medium text-gray-900">{format(day, 'EEE')}</div>
          <div
            className={`text-sm ${isToday(day) ? 'text-brand-primary font-semibold' : 'text-gray-600'}`}
          >
            {format(day, 'd')}
          </div>
        </div>
      ))}

      {/* Time slots */}
      {hours.map(hour => (
        <React.Fragment key={hour}>
          {/* Time label */}
          <div className="bg-white p-2 text-xs text-gray-600 border-r border-gray-100">
            {format(new Date().setHours(hour, 0, 0, 0), 'HH:mm')}
          </div>

          {/* Day columns for this hour */}
          {days.map(day => {
            const hourEvents = getEventsForDayAndHour(day, hour);
            const timeSlotDate = new Date(day);
            timeSlotDate.setHours(hour, 0, 0, 0);

            return (
              <div
                key={`${day.toISOString()}-${hour}`}
                className="bg-white min-h-[60px] p-1 cursor-pointer hover:bg-gray-50 transition-colors relative"
                onClick={() => onDateClick?.(timeSlotDate)}
              >
                {hourEvents.map(event => (
                  <button
                    key={event.id}
                    onClick={e => {
                      e.stopPropagation();
                      onEventClick(event.meeting);
                    }}
                    className="w-full text-left p-1 text-xs bg-brand-100 text-brand-800 rounded hover:bg-brand-200 transition-colors mb-1"
                  >
                    <div className="font-medium truncate">{event.meeting.title}</div>
                    <div className="flex items-center space-x-1 text-gray-600">
                      <Clock className="h-2.5 w-2.5" />
                      <span>{format(event.startTime, 'HH:mm')}</span>
                    </div>
                  </button>
                ))}

                {/* Click indicator for empty time slots */}
                {hourEvents.length === 0 && (
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                    <div className="text-xs text-gray-400 bg-white px-2 py-1 rounded shadow border">
                      Click to add meeting
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </React.Fragment>
      ))}
    </div>
  );
};

// Day View Component
const DayView: React.FC<{
  date: Date;
  events: CalendarEvent[];
  onEventClick: (meeting: Meeting) => void;
  onDateClick?: (date: Date) => void;
}> = ({ date, events, onEventClick, onDateClick }) => {
  const hours = Array.from({ length: 24 }, (_, i) => i);

  const getEventsForHour = (hour: number) => {
    return events.filter(
      event =>
        event.startTime.toDateString() === date.toDateString() &&
        event.startTime.getHours() === hour
    );
  };

  return (
    <div className="space-y-1">
      {hours.map(hour => {
        const hourEvents = getEventsForHour(hour);

        return (
          <div key={hour} className="flex items-start border-b border-gray-100 py-2">
            <div className="w-16 text-sm text-gray-600 pt-1">
              {format(new Date().setHours(hour, 0, 0, 0), 'HH:mm')}
            </div>

            <div className="flex-1 pl-4 relative">
              {hourEvents.length === 0 ? (
                <div
                  className="h-12 cursor-pointer hover:bg-gray-50 transition-colors rounded flex items-center justify-center group"
                  onClick={() => {
                    const timeSlotDate = new Date(date);
                    timeSlotDate.setHours(hour, 0, 0, 0);
                    onDateClick?.(timeSlotDate);
                  }}
                >
                  <div className="text-xs text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity bg-white px-2 py-1 rounded shadow border">
                    Click to add meeting
                  </div>
                </div>
              ) : (
                <div className="space-y-1">
                  {hourEvents.map(event => (
                    <button
                      key={event.id}
                      onClick={() => onEventClick(event.meeting)}
                      className="w-full text-left p-3 bg-brand-50 border border-brand-200 rounded-lg hover:bg-brand-100 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-brand-900">{event.meeting.title}</div>
                          <div className="flex items-center space-x-3 text-sm text-brand-700 mt-1">
                            <div className="flex items-center space-x-1">
                              <Clock className="h-4 w-4" />
                              <span>
                                {format(event.startTime, 'HH:mm')} -{' '}
                                {format(event.endTime, 'HH:mm')}
                              </span>
                            </div>
                            {event.meeting.location && (
                              <div className="flex items-center space-x-1">
                                <MapPin className="h-4 w-4" />
                                <span>{event.meeting.location}</span>
                              </div>
                            )}
                            {(event.meeting.projectMembers?.length || 0) +
                              (event.meeting.externalUsers?.length || 0) >
                              0 && (
                              <div className="flex items-center space-x-1">
                                <Users className="h-4 w-4" />
                                <span>
                                  {(event.meeting.projectMembers?.length || 0) +
                                    (event.meeting.externalUsers?.length || 0)}{' '}
                                  participants
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Helper functions
const getViewStartDate = (date: Date, view: CalendarView): Date => {
  if (view === 'day') {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  } else if (view === 'week') {
    const startOfWeek = new Date(date);
    startOfWeek.setDate(date.getDate() - date.getDay());
    return startOfWeek;
  } else {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }
};

const getViewEndDate = (date: Date, view: CalendarView): Date => {
  if (view === 'day') {
    const endOfDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    endOfDay.setHours(23, 59, 59, 999);
    return endOfDay;
  } else if (view === 'week') {
    const endOfWeek = new Date(date);
    endOfWeek.setDate(date.getDate() - date.getDay() + 6);
    endOfWeek.setHours(23, 59, 59, 999);
    return endOfWeek;
  } else {
    const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    endOfMonth.setHours(23, 59, 59, 999);
    return endOfMonth;
  }
};

export default MeetingCalendar;
